import { t } from "../lexique/langue.js";
/**
 * LA VUE DU LIEU — l'environnement, en une vignette (design §3).
 *
 * MÊME RAISON QUE L'EFFIGIE, MÊME REMÈDE. On joue sans illustrations la plupart du temps, et un
 * joueur qui lit trois cents nœuds sans jamais voir où il est finit par ne plus situer sa carte.
 * L'interface dessine donc le biome — les huit du §4.1 —, avec les hachures de la carte : le marais
 * de la vignette et le marais de la carte sont le même marais, ce qui n'est pas un détail quand on
 * annote l'un en regardant l'autre.
 *
 * ELLE EST PETITE ET MUETTE. Un bandeau bas, jamais plus du sixième de la hauteur, sans légende et
 * sans cadre. Elle SITUE, elle ne raconte pas : le texte raconte, et une vignette qui prendrait la
 * moitié de l'écran ferait exactement ce que le §1 interdit — entourer le texte au lieu de le
 * servir.
 *
 * UNE ILLUSTRATION POSÉE PASSE DEVANT. Comme pour l'effigie : `Illustration` d'abord, ceci ensuite.
 */

/** Les huit biomes du §4.1. Un inconnu se dessine en forêt : c'est le plus neutre des huit, et
 * refuser de dessiner laisserait le trou qu'on est en train de combler. */
export type Biome =
  | "foret"
  | "montagne"
  | "marais"
  | "desert"
  | "ruines"
  | "souterrain"
  | "urbain"
  | "cotier";

const BIOMES: Biome[] = ["foret", "montagne", "marais", "desert", "ruines", "souterrain", "urbain", "cotier"];

function estUnBiome(valeur: string | null | undefined): valeur is Biome {
  return typeof valeur === "string" && (BIOMES as string[]).includes(valeur);
}

/** Ce qui se découpe sur l'horizon, biome par biome. Deux plans : le LOIN, lavé, et le PRÈS, à
 * l'encre. Sans les deux, on obtient une frise, pas un paysage. */
const DECORS: Record<Biome, { loin: string; pres: string; horizon: string }> = {
  foret: {
    loin: "M0 44l16-16 14 16 12-11 15 11 18-14 16 14 20-12 18 12 22-15 20 15 21-11 18 11v10H0z",
    pres: "M22 52l9-16 9 16zM44 54l7-13 7 13zM168 52l10-17 10 17zM196 55l8-14 8 14z",
    horizon: "M0 52h240"
  },
  montagne: {
    loin: "M0 50l34-30 24 22 18-12 26 24 30-30 34 30 30-18 44 22v14H0z",
    pres: "M52 56l26-24 26 24zM138 58l20-19 20 19z",
    horizon: "M0 58h240"
  },
  marais: {
    loin: "M0 50c24-6 40 2 62 0 20-2 30-8 52-6 24 2 38 8 60 6 22-2 38-6 66-2v14H0z",
    pres: "M40 60v-16M44 46l-4-6M36 46l4-6M186 62v-18M190 46l-4-6M182 46l4-6M112 61v-13M108 50l4-6",
    horizon: "M0 60h240M18 66h34M74 68h40M150 66h50"
  },
  desert: {
    loin: "M0 52c30-14 52 6 86 2 30-4 44-16 70-12 28 4 52 14 84 8v14H0z",
    pres: "M60 62c8-8 18-8 26 0zM160 64c6-6 14-6 20 0z",
    horizon: "M0 60h240"
  },
  ruines: {
    loin: "M0 56h18V30h12v26h16V38h14v18h20V26h12v30h24V40h13v16h26V32h12v24h22V44h11v12h40v10H0z",
    pres: "M52 62h10v-8H52zM150 63h12v-9h-12z",
    horizon: "M0 62h240"
  },
  souterrain: {
    // Sous terre, le sujet n'est pas l'horizon : c'est la voûte. On la dessine, et le ciel
    // disparaît — c'est le seul biome où l'on ne voit pas le ciel, et ça se voit.
    loin: "M0 0h240v22c-40 0-40 18-120 18S40 22 0 22z",
    pres: "M30 22l6 14 6-14zM74 22l5 11 5-11zM160 22l6 13 6-13zM206 22l5 10 5-10z",
    horizon: "M0 62h240"
  },
  urbain: {
    loin: "M0 60h20V36h16v24h10V28h20v32h14V44h22v16h16V32h18v28h14V40h20v20h16V34h14v26h40v6H0z",
    pres: "M36 30h4v-8h-4zM106 26h4v-9h-4z",
    horizon: "M0 62h240"
  },
  cotier: {
    loin: "M0 54c26-4 44 4 70 2 24-2 36-10 62-8 26 2 44 10 70 8 16-1 26-4 38-4v14H0z",
    pres: "M0 62h60l14-18h22l10 18h134",
    horizon: "M0 62h240M20 70c6-4 12-4 18 0M60 74c6-4 12-4 18 0M170 70c6-4 12-4 18 0"
  }
};

export function Paysage({
  biome,
  lieu,
  nuit = false,
  className
}: {
  biome: string | null | undefined;
  /** Le nom du lieu — il sert à qui n'a pas l'image, pas à décorer le dessin : la vignette n'a pas
   * de légende, le titre du nœud est juste en dessous. */
  lieu?: string | null;
  nuit?: boolean;
  className?: string;
}) {
  const espece: Biome = estUnBiome(biome) ? biome : "foret";
  const decor = DECORS[espece];
  const classes = ["paysage", nuit ? "paysage--nuit" : "", className ?? ""].filter(Boolean).join(" ");

  return (
    <svg
      className={classes}
      viewBox="0 0 240 80"
      preserveAspectRatio="none"
      role="img"
      aria-label={lieu ? t("{lieu}, vue à la plume", { lieu }) : t("Le lieu, vu à la plume")}
    >
      <rect className="paysage__ciel" x="0" y="0" width="240" height="80" />
      <path className="paysage__loin" d={decor.loin} />
      {/* La terre porte la hachure du biome — la MÊME que la carte (`Defs`), montée une fois pour
          toute la page. Deux trames différentes pour un même marais, et la carte cesse d'être un
          aide-mémoire. */}
      <rect className="paysage__terre" x="0" y="56" width="240" height="24" />
      <rect className="paysage__trame" x="0" y="56" width="240" height="24" fill={`url(#biome-${espece})`} />
      <path className="paysage__horizon" d={decor.horizon} />
      <path className="paysage__silhouette" d={decor.pres} />
    </svg>
  );
}
